import type { TerminalAppModel } from "../state/ui-types";
import { matchesTransactionScope, transactionUnresolved } from "../wallet/transaction-tracker";

/** Presentation of controller decisions; this never determines action permission. */
export function SessionGuidance({ model, reviewingMint }: { model: TerminalAppModel; reviewingMint: boolean }) {
  const { wallet, mining, proofEligibility } = model;
  let title = "Ready to mine";
  let reason = "Start a challenge when you are ready. Mining can be repeated; it does not guarantee a valid proof.";
  const transaction = model.transactions.items.find((item) => transactionUnresolved(item) && wallet.address && wallet.chainId &&
    model.mintFee.collectionAddress && matchesTransactionScope(item, {
      account: wallet.address, chainId: wallet.chainId, collection: model.mintFee.collectionAddress, environment: model.environment,
    }));

  if (wallet.authState === "logout-pending" || wallet.authState === "logout-failed") {
    title = wallet.authState === "logout-pending" ? "Logging out" : "Logout needs retry";
    reason = wallet.error ?? "Waiting for the server to confirm logout.";
  } else if (wallet.authState === "checking" && !wallet.authenticated) {
    title = "Checking cookie session";
    reason = "Waiting for the server session and connected wallet to be verified.";
  } else if (wallet.authState === "unavailable" || wallet.authState === "configuration-error") {
    title = "Session check unavailable";
    reason = wallet.error ?? "Retry checking the cookie session.";
  } else if (wallet.status === "CONNECTING" || wallet.status === "SIGNING_IN") {
    title = "Complete the wallet request";
    reason = "Check your wallet. Signing in authenticates this session; it does not authorize a mint.";
  } else if (wallet.status === "WRONG_NETWORK") {
    title = "Switch to the required network";
    reason = "Use Switch network below, then Sign in to continue this session.";
  } else if (wallet.status === "SESSION_EXPIRED" || wallet.status === "CONNECTED" && wallet.sessionExpiresAt === null) {
    title = "Sign in to continue";
    reason = wallet.error ?? "Your wallet is connected, but this session needs authentication. Use the mining terminal's Sign in button.";
  } else if (wallet.status !== "CONNECTED") {
    title = "Connect to begin";
    reason = "Connect and sign in before starting a mining session.";
  } else if (model.transactions.blocked) {
    title = "Check transaction activity";
    reason = transaction?.message ?? model.transactions.storageWarning ?? "Resolve the existing transaction before sending another. Use the transaction controls below.";
  } else if (reviewingMint) {
    title = "Review the mint";
    reason = model.mintReview ? "Review the authorized fee and exact transaction. Wallet confirmation is a separate action." :
      "Prepare a quote to review its exact fee and transaction. Preparation does not send a transaction.";
  } else if (mining.phase === "VERIFIED") {
    title = model.environment !== "DEMO" && proofEligibility?.decision.state !== "ALLOWED" ?
      "Proof verified · check eligibility" : "Proof verified · choose a tier";
    reason = model.environment === "DEMO" ? "Choose a displayed eligibility tier to preview mint review. This demo cannot send a transaction." :
      proofEligibility?.loading ? "Refreshing proof eligibility. Tier selection uses the current backend decision." :
      proofEligibility?.decision.state === "ALLOWED" ? "Choose an available eligibility tier, then review mint conditions. A proof alone does not authorize minting." :
      proofEligibility?.error ?? proofEligibility?.decision.message ?? "Refresh proof eligibility before choosing a tier.";
  } else if (mining.phase === "MINING") {
    title = "Mining is active";
    reason = "You can pause the worker. A valid hash may arrive quickly or may not arrive before the challenge expires.";
  } else if (mining.phase === "PAUSED") {
    title = "Mining is paused";
    reason = "Resume the current challenge while it remains valid. Pausing does not extend its deadline.";
  } else if (mining.phase === "SUBMIT_RETRY") {
    title = mining.canRetrySubmission ? "Retry the saved proof submission" : "Proof recovery is unavailable";
    reason = mining.canRetrySubmission ? "Retry reuses the saved candidate for this challenge. It does not start new mining." :
      "The saved candidate is unavailable. Recovery cannot submit a replacement proof.";
  } else if (["STARTING", "CANDIDATE_FOUND", "SUBMITTING", "ERROR", "EXPIRED", "LOCKED", "UNAVAILABLE"].includes(mining.phase)) {
    title = mining.phase === "EXPIRED" ? "Request a new challenge" : "Mining session status";
    reason = mining.statusMessage;
  } else if (!model.runtime.publicMiningEnabled && !mining.demoOnly) {
    title = "Mining is unavailable";
    reason = "Public mining is disabled by the current deployment. Check System status for details.";
  }

  return <div className="session-guidance" role="status" aria-label="Mining session guidance">
    <strong>{title}</strong><p>{reason}</p>
  </div>;
}
